/**
 * PushPermissionDialog — our own explainer, shown before the OS notification
 * prompt.
 *
 * iOS grants exactly one system prompt per install: once the person taps
 * "Don't Allow" it never appears again and only a trip to Settings can undo
 * it. Firing it cold on launch, before they have seen a single product, spends
 * that one chance at the worst possible moment. This dialog says what the
 * notifications are actually for first; only "enable" reaches the OS prompt,
 * while "not now" leaves it unspent so `usePushRegistration` can offer again
 * after a cooling-off period.
 *
 * Rendered from PushRegistrationManager in App.tsx and driven entirely by the
 * state that `usePushRegistration()` returns — this component holds no logic
 * of its own.
 */

import { Bell } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

interface PushPermissionDialogProps {
  open: boolean;
  onEnable: () => void;
  onDismiss: () => void;
}

export function PushPermissionDialog({
  open,
  onEnable,
  onDismiss,
}: PushPermissionDialogProps) {
  const { t } = useLanguage();

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onDismiss(); }}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-push-permission">
        <DialogHeader>
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Bell className="h-7 w-7 text-primary" />
          </div>
          <DialogTitle className="text-center">
            {t("push.permission.title")}
          </DialogTitle>
          <DialogDescription className="text-center leading-relaxed">
            {t("push.permission.body")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            onClick={onEnable}
            className="w-full"
            data-testid="button-push-enable"
          >
            {t("push.permission.enable")}
          </Button>
          <Button
            variant="ghost"
            onClick={onDismiss}
            className="w-full"
            data-testid="button-push-later"
          >
            {t("push.permission.later")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
